import { Component, Input, OnInit } from '@angular/core';
import { StateService, TransitionService, UIRouter } from '@uirouter/angular';
import { SessionService, User, PortalSettings, Group, EDLGroupType, EdlFeatureFlags, ApplicationRoles, EdlCommonModalService, UserService } from '@edl-development/edl-common';
import { EdlItemPacketUserSummaryDTO } from '../../../../model/item-packet/edl-item-packet-user-summary-dto.model';
import { EdlItemPacketStatus } from '../../../../model/enum/item-packet/edl-item-packet-status.enum';
import { InComplianceStatus } from 'src/app/item-packets/common/enums/in-compliance-status.enum';
import { UserFormNavigation } from 'src/app/users/modify-user/enum/user-form-navigation.enum';
import { PortalHelpersService } from 'src/app/services/portal-helpers/portal-helpers.service';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CplUserStatusSummary } from 'src/app/model/user-compliance/cpl-user-status-summary.model';
import { CplUserStatus } from 'src/app/supervision-management/compliance-items/compliance-item/enums/cpl-user-status.enum';

export const USER_SUMMARY_FILTER_STATUS = EdlItemPacketStatus.PUBLISHED;

const TAB_REGISTRY = [
    {
        key: 'compliance',
        stateSuffix: '.compliance',
        relativeStateName: '^.compliance',
        elementId: 'compliance-tab',
        index: 0,
        childStateSuffixes: []
    },
    {
        key: 'requests',
        stateSuffix: '.requests',
        relativeStateName: '^.requests',
        elementId: 'requests-tab',
        index: 1,
        childStateSuffixes: ['.submissions']
    }
] as const;

const DEFAULT_TAB = TAB_REGISTRY[0].key;

type TabKey = typeof TAB_REGISTRY[number]['key'];
type TabConfiguration = typeof TAB_REGISTRY[number];

@Component({
    selector: 'edl-all-users-progress',
    templateUrl: './all-users-progress.component.html',
    styleUrls: ['./all-users-progress.component.scss']
})
export class AllUsersProgressComponent implements OnInit {
    @Input() user: User;
    @Input() userStatusSummary?: CplUserStatusSummary;
    @Input() itemPackets: EdlItemPacketUserSummaryDTO[] = [];

    selectedTab: TabKey = 'compliance';
    isLoading: boolean = false;
    isTransitioning: boolean = false;

    canArchiveSelf: boolean;                // Whether the user can archive themselves (stop volunteering)
    canViewUserProfile: boolean = false;    // Whether the user can view other users profile under user management

    // User display properties
    UserFormNavigation = UserFormNavigation;
    portalSettings: PortalSettings;
    groups: { [key: number]: Group[] } = {};
    CplUserStatus = CplUserStatus;
    EDLGroupType = EDLGroupType;
    EdlFeatureFlags = EdlFeatureFlags;

    // Item packet categorization
    canViewItemPackets: boolean = false;
    compliantItemPackets: EdlItemPacketUserSummaryDTO[] = [];
    nonCompliantItemPackets: EdlItemPacketUserSummaryDTO[] = [];
    
    // Tab display
    canViewComplianceItems: boolean = false;

    constructor(
        private stateService: StateService,
        private transitionService: TransitionService,
        private edlCommonModalService: EdlCommonModalService,
        private sessionService: SessionService,
        private portalHelpersService: PortalHelpersService,
        private userService: UserService,
        private toastr: ToastrService,
        private uiRouter: UIRouter
    ) {}

    ngOnInit() {
        this.portalSettings = this.sessionService.PortalSettings;
        this.canArchiveSelf = this.getCanArchiveSelf();
        this.canViewUserProfile = this.isTargetUserTheLoggedInUser || AccessRoleHelper.isAdmin(this.sessionService.user?.AccessRoleID);

        // Determine selected tab based on state name for initial load
        this.syncTabWithState();

        // Process user groups
        this.processUserGroups();

        this.canViewItemPackets = this.itemPackets && this.itemPackets.length > 0;
        this.compliantItemPackets = (this.itemPackets || []).filter(i => i.OverallStatus === InComplianceStatus.IN_COMPLIANCE);
        this.nonCompliantItemPackets = (this.itemPackets || []).filter(i => i.OverallStatus === InComplianceStatus.MISSING || i.OverallStatus === InComplianceStatus.OUT_OF_COMPLIANCE);

        // Loading indicator handling for tab switch
        this.transitionService.onSuccess({}, () => {
            this.syncTabWithState();
        });

        this.canViewComplianceItems = this.portalHelpersService.canViewComplianceItems(this.portalSettings);
    }

    get isTargetUserTheLoggedInUser(): boolean {
        return this.user?.UserID === this.sessionService.user?.UserID;
    }

    private getCanArchiveSelf = () => {
        return this.sessionService.user 
            && this.sessionService.user.UserID === this.user.UserID
            && this.sessionService.user.AccessRoleID == ApplicationRoles.USER
            && !this.user.Archived;
    }

    archiveSelf = () => {
        this.edlCommonModalService
            .confirm('Confirmation: Stop Volunteering', 'You will no longer be able to volunteer and you will no longer receive notifications from the system. Are you sure you want to continue?')
            .then(() => 
                firstValueFrom(this.userService.archiveSelf())
                    .then(() => this.stateService.go('public.logout'))
                    .catch(() => this.toastr.warning('You are not allowed to archive your account at this time. Please contact your system administrator for assistance.'))
            );
    }

    private processUserGroups() {
        if (this.user && this.user.UserGroups) {
            this.groups = this.user.UserGroups.reduce((acc, group) => {
            if (!acc[group.Type]) acc[group.Type] = [];
                acc[group.Type].push(group);
                return acc;
            }, {});
        }
    }

    private getTabConfig(tabKey: TabKey): TabConfiguration | undefined {
        return TAB_REGISTRY.find(t => t.key === tabKey);
    }

    private findTabForState(stateName: string): TabKey | null {
        for (const tabConfig of TAB_REGISTRY) {
            if (stateName.endsWith(tabConfig.stateSuffix)) {
                return tabConfig.key;
            }

            if (tabConfig.childStateSuffixes.length > 0) {
                for (const childSuffix of tabConfig.childStateSuffixes) {
                    if (stateName.endsWith(childSuffix)) {
                        return tabConfig.key;
                    }
                }
            }
        }
        return null;
    }

    private syncTabWithState() {
        const stateName = this.uiRouter.globals.current.name;
        this.selectedTab = this.findTabForState(stateName) ?? DEFAULT_TAB;
    }

    setLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    selectTab(tab: TabKey, focus: boolean = false) {
        const currentState = this.uiRouter.globals.current.name;
        const tabConfig = this.getTabConfig(tab);

        if (!tabConfig) {
            console.error(`Tab configuration not found for: ${tab}`);
            return;
        }

        if (!currentState.endsWith(tabConfig.stateSuffix) && !this.isTransitioning) {
            this.setLoading(true);
            this.isTransitioning = true;
            this.selectedTab = tab;

            const transition = this.stateService.go(tabConfig.relativeStateName, {}, { location: 'replace' });
            const resetLoadingState = () => {
                this.setLoading(false);
                this.isTransitioning = false;
            };

            if (transition && typeof transition.then === 'function') {
                transition.finally(resetLoadingState);
            } else {
                setTimeout(resetLoadingState, 100);
            }

            if (focus) {
                setTimeout(() => {
                    const tabButton = document.getElementById(tabConfig.elementId);
                    tabButton?.focus();
                });
            }
        }
    }

    onTabKeydown(event: KeyboardEvent, tab: TabKey) {
        const currentConfig = this.getTabConfig(tab);
        if (!currentConfig) return;

        const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (offset === 0) return;

        event.preventDefault();
        const targetTab = TAB_REGISTRY.find(t => t.index === currentConfig.index + offset);
        if (targetTab) {
            this.selectTab(targetTab.key, true);
        }
    }
}
